/**
 * @file LevelContentsSheet.tsx
 * @description Шторка «Что лежит на полке / стеллаже» после скана уровня
 *   (phase19.22, W14-9; ui-design §8): «Полка A-02 · 4 ячейки · 7 партий»,
 *   по каждой ячейке — партии с остатком.
 *
 * Уровень — не место хранения, а группа ячеек: содержимое собираем по
 * ячейкам (`GET bins/:id/contents`, только непустые, не больше шести
 * запросов разом). У стеллажа — список полок: тап раскрывает полку тем же
 * резолвером. Тап по ячейке открывает её шторку с действиями.
 */

import { ChevronRight, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import { binsApi, type BinContentRowDto } from "../../../lib/api-bins";
import type { ResolvedBinLevel } from "../../../lib/api-wms-scan";
import { pluralRu } from "../../../lib/plural";
import { formatMonthYear } from "../../../lib/receipt-expiry";

interface Props {
  level: ResolvedBinLevel;
  onOpenBin: (binId: string) => void;
  onOpenShelf: (address: string) => void;
  onClose: () => void;
}

/** Содержимое непустых ячеек уровня: id ячейки → строки. */
async function loadContents(binIds: string[]): Promise<Map<string, BinContentRowDto[]>> {
  const out = new Map<string, BinContentRowDto[]>();
  for (let i = 0; i < binIds.length; i += 6) {
    const chunk = binIds.slice(i, i + 6);
    const rows = await Promise.all(chunk.map((id) => binsApi.contents(id).catch(() => [])));
    chunk.forEach((id, index) => out.set(id, rows[index] ?? []));
  }
  return out;
}

export function LevelContentsSheet({ level, onOpenBin, onOpenShelf, onClose }: Props) {
  const [contents, setContents] = useState<Map<string, BinContentRowDto[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const isRack = level.level === "rack" && (level.shelves?.length ?? 0) > 0;
  const lots = level.bins.reduce((sum, bin) => sum + bin.lotsCount, 0);

  useEffect(() => {
    if (isRack) return;
    let cancelled = false;
    setLoading(true);
    void loadContents(level.bins.filter((b) => !b.isEmpty).map((b) => b.id)).then((map) => {
      if (cancelled) return;
      setContents(map);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [level, isRack]);

  const title = `${isRack ? "Стеллаж" : "Полка"} ${level.address}`;
  const bins = level.bins.length;

  return (
    <Sheet open onOpenChange={(open) => (!open ? onClose() : null)}>
      <SheetContent side="right" className="w-full max-w-none overflow-y-auto sm:max-w-[560px]">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <p className="text-sm text-on-surface-variant">
            {bins} {pluralRu(bins, ["ячейка", "ячейки", "ячеек"])} · {lots}{" "}
            {pluralRu(lots, ["партия", "партии", "партий"])}
          </p>
        </SheetHeader>

        {isRack ? (
          <ul className="flex flex-col gap-2">
            {level.shelves?.map((shelf) => (
              <li key={shelf.address}>
                <button
                  type="button"
                  onClick={() => onOpenShelf(shelf.address)}
                  className="flex min-h-14 w-full items-center justify-between gap-2 rounded-xl bg-surface-low px-3 text-left text-sm hover:bg-accent"
                >
                  <span className="font-mono">{shelf.address}</span>
                  <span className="flex items-center gap-1 text-on-surface-variant">
                    {shelf.binsCount} яч. · {shelf.lotsCount} п.
                    <ChevronRight className="size-4" aria-hidden />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="size-5 animate-spin text-primary" />
          </div>
        ) : (
          <ul className="flex flex-col gap-1">
            {level.bins.map((bin) => {
              const rows = contents.get(bin.id) ?? [];
              return (
                <li key={bin.id}>
                  <button
                    type="button"
                    onClick={() => onOpenBin(bin.id)}
                    className="flex min-h-14 w-full items-start gap-3 rounded-xl px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    <span className="w-20 shrink-0 font-mono">{bin.code}</span>
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      {rows.length === 0 ? <span className="text-on-surface-variant">пусто</span> : null}
                      {rows.map((row) => (
                        <span key={row.lotId} className="truncate">
                          {row.sku.productName}
                          {row.sku.displayName ? ` ${row.sku.displayName}` : ""} · {row.batchNumber} ·{" "}
                          <span className={row.expiresAt ? "" : "text-warning"}>до {formatMonthYear(row.expiresAt)}</span>
                          {" · "}
                          {Number(row.quantity).toLocaleString("ru-RU")} шт
                        </span>
                      ))}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  );
}
